/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import "./index.css";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { Provider } from "react-redux";
import { Toaster } from "sonner";
import { TailwindIndicator } from "@/components/tailwind-indicator";
import { SocketProvider } from "@/hooks/use-socket.tsx";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { store } from "@/redux/store";
import ErrorBoundary from "@/components/ErrorBoundary";
import { OfflineIndicator } from "@/components/common/OfflineIndicator";
import "./i18n";
import App from "./App";
import { loadProductIdConfig } from "@/services/productIdConfig";

const isSecureEnv =
  import.meta.env.VITE_REACT_ENV === "production" ||
  import.meta.env.VITE_REACT_ENV === "staging";

if (isSecureEnv && import.meta.env.VITE_REACT_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_REACT_SENTRY_DSN,
    environment: import.meta.env.VITE_REACT_ENV,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: import.meta.env.VITE_REACT_ENV === "staging" ? 1.0 : 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Load the canonical QLM<->Stripe product-id maps from the backend (P-1).
// Non-blocking: the baked-in constants are the synchronous fallback, so render
// proceeds immediately and getters return correct values either way.
void loadProductIdConfig();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <CurrencyProvider>
          <SocketProvider>
            <App />
            <Toaster />
            <TailwindIndicator />
            <OfflineIndicator position="bottom" />
          </SocketProvider>
        </CurrencyProvider>
      </Provider>
    </ErrorBoundary>
  </StrictMode>,
);
